// TOEFL iBT Mock 6 — Reading (2026 format, two timed modules)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, memos).
//   academic – Read an Academic Passage: short passage, 5 questions
//              (the last question is a "where does this sentence fit?" item;
//              the [A]–[D] markers are part of the passage text).
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2.
//
// NOTE: Mock Test 6 in the PDF has a single Module 2, so `upper` and `lower`
// currently point to the same content. Replace `lower` with easier material
// if you want true adaptive routing.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

// ── Module 1 ─────────────────────────────────────────────────
const module1 = [
    {
      id: "m1-words",
      type: "words",
      title: "Auroras",
      text: "Auroras, often referred to as the northern or southern lights, present one of the most spectacular visual phenomena in Earth's [[upper]] atmosphere. These luminous displays are primarily [[generated]] when charged particles emitted by the Sun, known as solar [[winds]], collide with gaseous molecules in our planet's magnetic [[field]]. As these energetic particles strike oxygen and nitrogen atoms, they transfer [[energy]], which is [[subsequently]] released in the form of brilliant, undulating lights. The specific [[colors]] observed during an auroral event depend on the type of gas involved and the [[altitude]] of the collision. For [[instance]], high-altitude oxygen produces rare red auroras, while lower-altitude oxygen is [[responsible]] for the more common green hues. These dynamic light [[waves]] not only captivate observers but also provide valuable [[data]] regarding geomagnetic storms and solar [[activity]].",
    },
    {
      id: "m1-daily1",
      type: "daily",
      title: "ID Card Pickup Hours",
      text: `From: Student Services. Subject: New ID Card Pickup Hours. Dear Students, The ID Card Office will offer evening pickup hours during the first week of classes. From Monday through Thursday, students may collect new ID cards between 5:00 and 7:00 p.m. in the Student Services lobby. Daytime hours remain unchanged. Please bring a photo ID and your student number. Cards requested after noon will be ready the following business day.`,
      questions: [
        {
          question: "Why did Student Services send this email?",
          options: [
            "To tell students that daytime office hours are canceled",
            "To explain how to request a student number",
            "To announce extra evening hours for picking up ID cards",
            "To warn students that ID cards will expire soon",
          ],
          correct: 2,
        },
        {
          question: "Where can students pick up ID cards during the evening hours?",
          options: [
            "At the campus library desk",
            "In their residence hall office",
            "At the registrar window",
            "In the Student Services lobby",
          ],
          correct: 3,
        },
        {
          question: "What can be inferred if a student requests a card at 2:00 p.m. on Tuesday?",
          options: [
            "The card can be picked up immediately at 5:00 p.m.",
            "The card will be ready on Wednesday.",
            "The student must request a new student number.",
            "The office will mail the card to the student.",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "m1-daily2",
      type: "daily",
      title: "Harbor Hill Bakery Post",
      text: `Harbor Hill Bakery (@harborhillbakery)

Big news, neighbors! After three years of selling our sourdough at the Saturday farmers market, we're opening a real storefront at 48 Main Street on Friday, March 7! To celebrate, the first 50 customers get a free cinnamon roll, and every loaf will be half price until noon.

We'll be open Tuesday through Sunday, 7 a.m. to 3 p.m. Our Saturday market stand will continue through the spring, so you can still find us there. Want a custom cake? Orders need 72 hours' notice. Message us or call 555-0171.

Thanks to everyone who supported us from our tiny folding table. We can't wait to welcome you inside! #SupportLocal #FreshBread`,
      questions: [
        {
          question: "What is the main purpose of the post?",
          options: [
            "To announce a new market location",
            "To advertise custom cakes",
            "To announce a new storefront and opening-day offers",
            "To say that the bakery is closing for spring",
          ],
          correct: 2,
        },
        {
          question: "How can a customer get a half-price loaf?",
          options: [
            "Order a custom cake",
            "Visit the market stand on Saturday",
            "Show the post to the cashier on Tuesday",
            "Buy one before noon on opening day",
          ],
          correct: 3,
        },
        {
          question: "What can be inferred about Harbor Hill Bakery?",
          options: [
            "It began as a small market stand.",
            "It has several other storefronts.",
            "It is closed on weekends.",
            "It only sells cinnamon rolls.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-academic",
      type: "academic",
      title: "Mycelium Building Materials",
      text: `The construction industry is responsible for a significant portion of global carbon emissions, primarily due to the energy-intensive manufacturing of conventional materials like concrete and steel. In response, architects and engineers are increasingly exploring bio-fabrication, a process that uses living organisms to manufacture structural materials. One of the most promising developments in this field involves mycelium, the vegetative, root-like network of fungi. Mycelium can be cultivated to form solid, lightweight blocks that offer a sustainable alternative for various construction and insulation requirements.

The production of mycelium-based materials is relatively straightforward and requires minimal energy. Agricultural byproducts, such as corn husks, hemp shards, or sawdust, are gathered and inoculated with fungal spores. [A] The mycelium feeds on this organic waste, extending a dense, microscopic web of fibers that binds the loose organic material together. [B] When the mixture is placed into a mold, it conforms to the desired shape as it expands over a period of about a week. [C] Once the composite reaches the correct density, it is extracted from the mold and subjected to high heat. [D] This baking process kills the fungus and halts the growth entirely, ensuring that the final product is biologically inactive and will not sprout mushrooms or decompose unexpectedly.

Mycelium composites possess several advantageous physical properties. They are surprisingly durable, lightweight, and provide exceptional thermal and acoustic insulation, making them ideal for interior wall panels. In addition, they exhibit a high degree of natural fire resistance compared to synthetic insulation or traditional wood framing. Perhaps most importantly, from an environmental perspective, mycelium materials are entirely biodegradable. At the end of a building's useful life, these components can simply be broken down and composted, safely returning nutrients to the earth rather than occupying space in a landfill.

Despite these clear environmental benefits, mycelium construction materials currently face practical limitations. Their compressive strength, the capacity to withstand heavy, downward pressure, is significantly lower than that of concrete or steel. Consequently, standard mycelium bricks cannot safely support the load-bearing foundations of multi-story buildings. Nevertheless, researchers are actively experimenting with hybrid composites, combining the fungal network with stronger natural fibers or reinforcing structures, to enhance their load-bearing capabilities and pave the way for wider commercial adoption.`,
      questions: [
        {
          question: "The word \"exhibit\" in the passage is closest in meaning to which word?",
          options: [
            "require",
            "display",
            "acquire",
            "restrict",
          ],
          correct: 1,
        },
        {
          question: "According to paragraph 3, what is a key environmental advantage of mycelium materials at the end of a building's typical lifespan?",
          options: [
            "They can be melted down and reformed into new building panels.",
            "They burn cleanly without releasing toxic chemicals into the air.",
            "They naturally decompose into the soil as compost.",
            "They provide a permanent foundation that does not need replacing.",
          ],
          correct: 2,
        },
        {
          question: "According to the passage, all of the following are mentioned as physical properties or benefits of mycelium composites EXCEPT:",
          options: [
            "They provide excellent sound insulation.",
            "They are lighter than many conventional materials.",
            "They can safely support the foundations of multi-story structures.",
            "They are naturally resistant to fire.",
          ],
          correct: 2,
        },
        {
          question: "Look at the four squares [A], [B], [C], and [D] that indicate where the following sentence could be added to the passage. \"During this growth phase, the mold is kept in a dark, climate-controlled environment to accelerate the binding process.\" Where would the sentence best fit?",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 2,
        },
        {
          question: "What can be inferred from paragraph 4 about the future use of mycelium in commercial construction?",
          options: [
            "It will likely completely replace concrete and steel within a decade.",
            "Its use in major construction projects will likely increase if its structural strength can be improved.",
            "It will continue to be restricted strictly to temporary, single-story structures.",
            "Researchers will abandon the material if it cannot be made entirely fireproof.",
          ],
          correct: 1,
        },
      ],
    },
];

// ── Module 2 ─────────────────────────────────────────────────
const module2 = [
    {
      id: "m2-words",
      type: "words",
      title: "Language Change",
      text: "All living languages change over time, and this process affects sounds, vocabulary, and grammar. [[New]] words [[appear]] when [[speakers]] invent [[terms]] for [[new]] technology or [[borrow]] useful [[expressions]] from [[neighboring]] cultures. [[Pronunciation]] also [[shifts]] slowly, so [[that]] the [[speech]] of one [[generation]] may [[sound]] unusual to [[the]] next. [[Over]] many [[centuries]], these small differences can accumulate until two groups who once shared a language can no longer understand each other. This is how Latin gradually developed into several separate modern languages, including Spanish, French, and Italian. Linguists study such patterns to reconstruct ancient tongues and to learn how human communities have moved and interacted throughout history.",
    },
    {
      id: "m2-daily1",
      type: "daily",
      title: "Damaged Blender Reply",
      text: `Subject: Re: Order #48213 - Damaged Blender

Dear Ms. Okafor,

Thank you for contacting Kitchen Valley. I am sorry that your Turbo 600 blender arrived with a cracked jar. I have reviewed the photographs you sent, and I can confirm that the damage appears to have happened during shipping. We will send a replacement jar at no charge. It should arrive within four to six business days, and you will receive a tracking number by email tomorrow.

You do not need to return the damaged jar; please recycle it. If you would rather have a full refund for the entire blender, reply to this message by Friday and we will email a prepaid return label. Please keep the motor base for use with the new jar.

Sincerely,

Dennis Rowe

Customer Care Team`,
      questions: [
        {
          question: "What is the main purpose of the email?",
          options: [
            "To ask Ms. Okafor to send photographs",
            "To explain how the company will resolve a damaged item",
            "To announce a new blender model",
            "To explain the shipping policy for all orders",
          ],
          correct: 1,
        },
        {
          question: "What should Ms. Okafor do with the cracked jar?",
          options: [
            "Return it using a prepaid label",
            "Keep it until Friday",
            "Mail it to Dennis Rowe",
            "Recycle it",
          ],
          correct: 3,
        },
        {
          question: "What should Ms. Okafor do if she wants a refund?",
          options: [
            "Contact the shipping company",
            "Throw away the motor base",
            "Reply to the email by Friday",
            "Wait for the tracking number",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m2-daily2",
      type: "daily",
      title: "Ecology Syllabus Excerpt",
      text: `BIO 210: Introduction to Ecology - Syllabus Excerpt (Fall)

Grading: Weekly quizzes 20%, lab reports 30%, midterm exam 20%, final project 30%.

Attendance: Attendance in lab sessions is mandatory. Missing more than two labs without documentation will lower your final grade by one letter.

Late work: Lab reports are due at the start of your lab session. Late reports lose 10% per day, up to three days; after that, no credit is given. Students with medical or family emergencies should contact the instructor as soon as possible to arrange an extension.

Office hours: Tuesdays 2:00-4:00 p.m., Room 318 Science Hall, or by appointment.

Required text: Principles of Ecology, 5th edition (available on reserve at the library).`,
      questions: [
        {
          question: "What happens to a lab report turned in two days late?",
          options: [
            "It loses 20% of its value.",
            "It loses 10% of its value.",
            "It receives no credit.",
            "It is accepted without a penalty.",
          ],
          correct: 0,
        },
        {
          question: "What should a student with a family emergency do?",
          options: [
            "Skip the final project",
            "Contact the instructor as soon as possible",
            "Wait until office hours on Tuesday",
            "Submit the report to the library",
          ],
          correct: 1,
        },
        {
          question: "In the syllabus, the word \"mandatory\" is closest in meaning to",
          options: [
            "optional",
            "recorded",
            "brief",
            "required",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m2-academic",
      type: "academic",
      title: "Finding Worlds Beyond the Solar System",
      text: `Until 1995, the only planets known to astronomers were those orbiting the Sun. That year, Michel Mayor and Didier Queloz announced the discovery of a planet orbiting the Sun-like star 51 Pegasi, an achievement later recognized with a Nobel Prize in Physics. Since then, thousands of planets beyond our solar system, known as exoplanets, have been confirmed.

Exoplanets are extremely difficult to observe directly because they are faint and lie close to stars that are billions of times brighter. Astronomers have therefore developed indirect detection methods. One of the most productive is the transit method. [A] When a planet passes in front of its star as seen from Earth, it blocks a small fraction of the starlight, causing a temporary dip in the star's apparent brightness. [B] The size of the dip reveals the planet's diameter relative to the star, while the interval between repeated dips reveals its orbital period.

Space telescopes have greatly expanded this work. NASA's Kepler mission, launched in 2009, monitored a vast number of stars in one region of the sky and revealed that planets are common in our galaxy. [C] Its successor, TESS, surveys nearly the entire sky, concentrating on brighter, nearby stars that are easier to study afterward. The James Webb Space Telescope, launched in 2021, can analyze starlight filtering through an exoplanet's atmosphere during a transit, revealing which gases are present.

These discoveries have reshaped ideas about planetary systems. Astronomers have found "hot Jupiters," giant planets orbiting extremely close to their stars, a type that no one expected from the example of our own solar system. [D] They have also located planets in the habitable zone, the range of distances at which liquid water could exist on a planet's surface. Whether any such world supports life remains unknown, but atmospheric studies may eventually provide clues.`,
      questions: [
        {
          question: "The word \"confirmed\" in the passage is closest in meaning to",
          options: [
            "rejected",
            "verified",
            "photographed",
            "named",
          ],
          correct: 1,
        },
        {
          question: "According to paragraph 2, what does the interval between repeated dips in starlight reveal?",
          options: [
            "The planet's atmosphere",
            "The planet's diameter",
            "The planet's orbital period",
            "The star's age",
          ],
          correct: 2,
        },
        {
          question: "What can be inferred from paragraph 4 about the solar system?",
          options: [
            "It is not necessarily a typical model for other planetary systems.",
            "It contains more planets than any other system.",
            "It includes a hot Jupiter close to the Sun.",
            "It lies within the habitable zone of another star.",
          ],
          correct: 0,
        },
        {
          question: "Why does the author mention that stars are billions of times brighter than exoplanets in paragraph 2?",
          options: [
            "To show that transits cannot be measured",
            "To argue that Kepler was unnecessary",
            "To describe the size of the habitable zone",
            "To explain why indirect detection methods were needed",
          ],
          correct: 3,
        },
        {
          question: "Where would the following sentence best fit? \"Their existence forced astronomers to reconsider how planets form and move within their systems.\"",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 3,
        },
      ],
    },
];

export const readingModules = {
  module1,
  upper: module2,
  lower: module2,
};

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}